/*
 * Copyright (c) 2022-2022. Revo Digital
 * ---
 * Author: gabriele
 * File: RichTextEditor.tsx
 * Project: mle-client-n
 * Committed last: 2022/2/4 @ 1517
 * ---
 * Description:
 */

import { Editor }                     from '@tinymce/tinymce-react';
import React, { useCallback, useRef } from 'react';
import { Button, IconName, Intent }   from '@blueprintjs/core';
import { Editor as StateManager }     from '../../../store/observables/Editor';

interface IActionButton {
  text: string;
  icon?: IconName;
  onClick: () => void;
}

interface Props {
  editor: StateManager;
  setter: (html: string, style: string) => void;
  initialValue?: string;
  disableExit?: boolean;
  onExit?: () => void;
  className?: string;
  actions?: IActionButton[];
  mini?: boolean;
}

export const ActionButton = (props: IActionButton) => {
  return (
    <Button small icon={ props.icon } onClick={ props.onClick }>
      {
        props.text
      }
    </Button>
  );
};

export const RichTextEditor = (props: Props) => {
  const editorRef = useRef<any>(null);

  const handleApply = useCallback(() => {
    if (editorRef.current) {
      props.setter(editorRef.current.getContent(),
        'p { margin: 0px; padding: 0px}');

      if (props.onExit && props.disableExit !== true) props.onExit();
    }
  }, [ editorRef ]);

  const handleSave = useCallback(() => {
    if (editorRef.current)
      props.setter(editorRef.current.getContent(),
        'p { margin: 0px; padding: 0px}');
  }, []);

  const handleExit = useCallback(() => {
    if (props.onExit)
      props.onExit();
  }, [ props.onExit ]);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    // Trigger apply on ctrl + s
    if (event.key === 's' && event.ctrlKey) {
      handleSave();
      event.preventDefault();
      event.stopImmediatePropagation();
      event.stopPropagation();
    }
  }, []);

  const getToolbarButtons = useCallback((): string => {
    if (!props.mini)
      return 'bold italic  underline | forecolor backcolor | alignleft aligncenter alignright alignjustify | styleselect | fontselect fontsizeselect|  ' +
             ' bullist numlist | lineheight |' +
             'removeformat | code';
    else return 'bold italic underline | alignleft alignjustify | forecolor';
  }, [ props.mini ]);

  return (
    <div className={ props.className }>
      <Editor
        onInit={ (evt, editor) => editorRef.current = editor }
        initialValue={ props.initialValue }
        onSaveContent={ handleSave }
        onKeyDown={ handleKeyDown }
        init={ {
          height: 500,
          menubar: false,
          plugins: [
            'advlist autolink lists link image charmap print preview anchor',
            'searchreplace visualblocks code fullscreen',
            'insertdatetime media table paste code help wordcount textcolor'
          ],
          toolbar: getToolbarButtons(),
          contextmenu: 'copy paste cut | bold italic underline | forecolor alignleft aligncenter alignjustify | removeformat',
          content_style: '@import url(\'https://fonts.googleapis.com/css2?family=Lato:wght@900&family=Roboto&display=swap\'); body { font-family:Helvetica,Arial,sans-serif; font-size:14px; margin: 10px }'
        } }
      />
      <div className={ 'mt-2 space-x-2 flex flex-row justify-end' }>
        {
          props.actions?.map((it) => <ActionButton text={ it.text }
                                                   onClick={ it.onClick }
                                                   icon={ it.icon }/>)
        }
        {
          props.disableExit !== true &&
          <Button small onClick={ handleExit }>
            Chiudi
          </Button>
        }
        <Button small onClick={ handleApply } intent={ Intent.PRIMARY }>
          Applica
        </Button>
      </div>
    </div>
  );
};
